// Day/night: a darkness layer over the world view with soft holes punched around players and
// light-giving objects, plus an additive warm glow for flames. Radville's night look, in Phaser.
import Phaser from 'phaser';
import { Settings } from './settings.ts';

export type Light = { x: number; y: number; r: number; warm: boolean; tint?: number };

const PAD = 64;
const NIGHT_COLOR = 0x070a16;

export class Lighting {
  private dark: Phaser.GameObjects.RenderTexture | null = null;
  private glow: Phaser.GameObjects.RenderTexture | null = null;
  private brush: Phaser.GameObjects.Image;
  private w = 0;
  private h = 0;

  constructor(private scene: Phaser.Scene) {
    if (!scene.textures.exists('light')) {
      const c = scene.textures.createCanvas('light', 256, 256)!;
      const ctx = c.getContext();
      const g = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.45, 'rgba(255,255,255,0.85)');
      g.addColorStop(0.75, 'rgba(255,255,255,0.35)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 256, 256);
      c.refresh();
    }
    this.brush = scene.make.image({ key: 'light', add: false }).setOrigin(0.5);
  }

  private ensure(w: number, h: number): void {
    if (this.dark && w <= this.w && h <= this.h) return;
    this.w = Math.ceil(w / 128) * 128;
    this.h = Math.ceil(h / 128) * 128;
    this.dark?.destroy();
    this.glow?.destroy();
    this.dark = this.scene.add.renderTexture(0, 0, this.w, this.h).setOrigin(0).setDepth(9e5);
    this.glow = this.scene.add.renderTexture(0, 0, this.w, this.h).setOrigin(0).setDepth(9e5 + 1).setBlendMode(Phaser.BlendModes.ADD);
  }

  /** daylight: 1 = noon (layer hidden), ~0.12 = darkest night. */
  draw(daylight: number, lights: Light[], time: number): void {
    const cam = this.scene.cameras.main;
    const view = cam.worldView;
    const darkness = Math.min(0.9, (1 - daylight) * 1.02) * Settings.get().night;
    if (darkness < 0.02) {
      this.dark?.setVisible(false);
      this.glow?.setVisible(false);
      return;
    }
    this.ensure(view.width + PAD * 2, view.height + PAD * 2);
    const ox = Math.floor(view.x - PAD), oy = Math.floor(view.y - PAD);
    const dark = this.dark!, glow = this.glow!;
    dark.setVisible(true).setPosition(ox, oy).clear().fill(NIGHT_COLOR, darkness);
    glow.setVisible(true).setPosition(ox, oy).clear();
    glow.setAlpha(Math.min(1, darkness * 1.3));
    for (const l of lights) {
      if (l.x < view.x - l.r || l.x > view.right + l.r || l.y < view.y - l.r || l.y > view.bottom + l.r) continue;
      // Flames flicker a little; each light has its own phase.
      const flicker = l.warm ? 1 + Math.sin(time * 0.009 + l.x * 0.13) * 0.04 + Math.sin(time * 0.023 + l.y) * 0.03 : 1;
      const r = l.r * flicker;
      this.brush.setScale((r * 2) / 256).setPosition(l.x - ox, l.y - oy).setAlpha(1).clearTint();
      dark.erase(this.brush);
      if (l.warm) {
        this.brush.setScale((r * 1.3) / 256).setTint(l.tint ?? 0xff8a30).setAlpha(l.tint ? 0.6 : 0.45);
        glow.draw(this.brush);
      }
    }
  }
}
